import { useEffect, useState, type JSX } from 'react';
import {
  Filter,
  DropdownCellOptionType,
  type OptionGroup,
} from '@doxyme/ui/components/foundations';
import { languageOptions as allLanguageOptions } from '@doxyme/ui/config';
import { Icon } from '@doxyme/icons';
import { useIntl, useLocale } from '@doxyme/locale';
import { usePreviouslySelected } from './usePreviouslySelected';

export type LanguageDropdownProps = {
  previouslySelectedLocalStorageKey: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  allowedLanguageCodes?: string[];
};

export const LanguageDropdown = ({
  previouslySelectedLocalStorageKey,
  onChange,
  disabled,
  allowedLanguageCodes,
}: LanguageDropdownProps): JSX.Element => {
  const { locale } = useLocale();
  const { formatMessage } = useIntl();

  const languageOptions = allowedLanguageCodes
    ? allLanguageOptions.filter((option) =>
        allowedLanguageCodes.includes(option.value),
      )
    : allLanguageOptions;

  const userLocale = languageOptions.some((option) => option.value === locale)
    ? locale
    : null;

  const [previouslySelected, updatePreviouslySelected] = usePreviouslySelected(
    previouslySelectedLocalStorageKey,
  );

  const [selectedValue, setSelectedValue] = useState<string | null>(() => {
    if (
      languageOptions.some((option) => option.value === previouslySelected[0])
    ) {
      return previouslySelected[0]!;
    }
    return userLocale ?? 'en';
  });

  const onOptionClick = (value: string): void => {
    setSelectedValue(value);
    updatePreviouslySelected(value);
  };

  useEffect(() => {
    selectedValue !== null && onChange(selectedValue);
  }, [selectedValue]);

  // Only show previously selected items that are still in our filtered options
  const allowedPreviouslySelected = previouslySelected
    .map((value) => languageOptions.find((option) => option.value === value))
    .filter(Boolean);

  const options =
    allowedPreviouslySelected.length > 0
      ? ([
          {
            title: formatMessage({
              id: 'inviteVia.previouslySelected',
            }),
            options: allowedPreviouslySelected,
          },
          {
            title: formatMessage({
              id: 'inviteVia.all',
            }),
            options: languageOptions,
          },
        ] as OptionGroup[])
      : languageOptions;

  return (
    <Filter
      type={DropdownCellOptionType.Radiobutton}
      icon={<Icon.Globe type='stroke' size={16} />}
      selected={selectedValue}
      options={options}
      onOptionClick={onOptionClick}
      dropdownPlacement='bottom-end'
      disabled={disabled}
    />
  );
};
